<script lang="ts" setup>
import type { FilterSchemaCollection } from './utils/filter-schema';
import { useTemplateRef } from 'vue';
import { DisplayTemplateConfigurationError, isEmptyFilter, isFieldFilter, isLogicalFilter } from '#layers/data-model/public';
import { injectFilterRoot } from '#layers/filters/composables/use-filter-root';
import { useRelatedItemMatch } from '#layers/filters/composables/use-related-item-match';
import { ScrollFadeAxis, useScrollFade } from '#layers/ui/public';

defineProps<{
	collection: FilterSchemaCollection;
}>();

const {
	filter,
	addFilter,
	addCondition,
	addGroup,
	removeAt,
	updateAt,
	clear: clearFilter,
	replacementVersion,
	isPendingAdvance,
	clearPendingAdvance,
} = injectFilterRoot();
const { isRelatedItemMatch } = useRelatedItemMatch();

// The scrollbar is hidden, so the fade is the only cue that clauses continue past the edge.
// The clause row is measured separately because adding a clause widens it without resizing
// the scroll container, which is sized by the bar around it.
const clausesRef = useTemplateRef<HTMLElement>('clauses');
const clauseRowRef = useTemplateRef<HTMLElement>('clauseRow');

const { style: scrollFadeStyle } = useScrollFade(clausesRef, {
	axis: ScrollFadeAxis.Horizontal,
	content: [clauseRowRef],
});
</script>

<template>
	<NuxtErrorBoundary>
		<template #error>
			<span class="flex items-center gap-2">
				<DisplayTemplateConfigurationError :error="{ message: $t('filters.invalid_filter') }" />
				<DButton
					variant="ghost"
					color="neutral"
					size="xs"
					@click="clearFilter"
				>
					{{ $t('filters.clear_filter') }}
				</DButton>
			</span>
		</template>

		<div class="flex flex-1 shrink-0 items-center gap-2 overflow-hidden" :class="{ '-ms-4.5': isEmptyFilter(filter) }">
			<div
				ref="clauses"
				class="scrollbar-hidden overflow-x-auto mask-r-from-(--scroll-fade-end) mask-l-from-(--scroll-fade-start)"
				:style="scrollFadeStyle"
			>
				<div ref="clauseRow" class="flex w-max items-center gap-2">
					<template
						v-for="(subFilter, index) in filter._and"
						:key="`${replacementVersion}:${index}`"
					>
						<FilterAdvanced
							v-if="isLogicalFilter(subFilter)"
							:model-value="subFilter"
							:collection
							:auto-advance="isPendingAdvance(index)"
							@update:model-value="updateAt(index, $event)"
							@remove="removeAt(index)"
							@auto-advance-done="clearPendingAdvance"
						/>
						<FilterRelatedItem
							v-else-if="isFieldFilter(subFilter) && isRelatedItemMatch(subFilter, collection)"
							:model-value="subFilter"
							:collection
							@update:model-value="updateAt(index, $event)"
							@remove="removeAt(index)"
						/>
						<FilterSimple
							v-else-if="isFieldFilter(subFilter)"
							:model-value="subFilter"
							:collection
							:auto-advance="isPendingAdvance(index)"
							@update:model-value="updateAt(index, $event)"
							@remove="removeAt(index)"
							@auto-advance-done="clearPendingAdvance"
						/>
					</template>
				</div>
			</div>
			<FilterAdd
				:collection
				@add-filter="addFilter"
				@add-group="addGroup"
				@add-condition="addCondition"
			/>
		</div>
	</NuxtErrorBoundary>
</template>
